import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Mail,
    Search,
    Filter,
    Eye,
    MessageSquare,
    Clock,
    User,
    Phone,
    Calendar,
    CheckCircle,
    AlertCircle,
    XCircle,
    Store,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import { 
    useGetMarketplaceInquiriesQuery,
    useRespondMarketplaceInquiryMutation,
    MarketplaceInquiry,
    MarketplaceInquiriesParams
} from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import toast from 'react-hot-toast';

const MarketplaceInquiries: React.FC = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'in_progress' | 'resolved'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [selectedInquiry, setSelectedInquiry] = useState<MarketplaceInquiry | null>(null);
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyStatus, setReplyStatus] = useState<'new' | 'in_progress' | 'resolved'>('in_progress');

    // Fetch all marketplace inquiries (no pagination params for client-side filtering)
    const { 
        data: inquiriesData, 
        isLoading, 
        error, 
        refetch 
    } = useGetMarketplaceInquiriesQuery({
        page: 1,
        limit: 10000 // Fetch all for client-side filtering
    });

    const [respondInquiry, { isLoading: isResponding }] = useRespondMarketplaceInquiryMutation();

    const allInquiries = inquiriesData?.data?.inquiries || [];

    // Client-side filtering
    const filteredInquiries = allInquiries.filter((inquiry: MarketplaceInquiry) => {
        const matchesSearch =
            inquiry.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inquiry.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inquiry.contact_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inquiry.listing?.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inquiry.user?.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    // Client-side pagination
    const totalPages = Math.ceil(filteredInquiries.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedInquiries = filteredInquiries.slice(startIndex, startIndex + itemsPerPage);

    // Helper function to get page numbers for pagination
    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }
        
        return pages;
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'new':
                return <Badge variant="warning" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />New</Badge>;
            case 'in_progress':
                return <Badge variant="default" className="flex items-center gap-1"><Clock className="w-3 h-3" />In Progress</Badge>;
            case 'resolved':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Resolved</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const handleViewInquiry = (inquiry: MarketplaceInquiry) => {
        navigate(`/admin/marketplace-inquiries/view/${inquiry.inquiry_id}`);
    };

    const handleRespond = (inquiry: MarketplaceInquiry) => {
        setSelectedInquiry(inquiry);
        setReplyMessage(inquiry.admin_response || '');
        setReplyStatus(inquiry.status);
        setShowReplyModal(true);
    };

    const handleSendReply = async () => {
        if (!selectedInquiry) return;

        try {
            await respondInquiry({
                inquiry_id: selectedInquiry.inquiry_id,
                status: replyStatus,
                admin_response: replyMessage.trim() || undefined
            }).unwrap();
            toast.success('Inquiry response saved successfully');
            setShowReplyModal(false);
            setReplyMessage('');
            setSelectedInquiry(null);
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to respond to inquiry');
        }
    };

    // Statistics
    const newCount = allInquiries.filter(i => i.status === 'new').length;
    const inProgressCount = allInquiries.filter(i => i.status === 'in_progress').length;
    const resolvedCount = allInquiries.filter(i => i.status === 'resolved').length;

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading inquiries. Please try again.</p>
                    <button
                        onClick={() => refetch()}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-8">
                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">New Inquiries</p>
                                <p className="text-3xl font-bold text-yellow-600">{newCount}</p>
                            </div>
                            <div className="p-3 bg-yellow-100 rounded-full">
                                <AlertCircle className="w-6 h-6 text-yellow-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">In Progress</p>
                                <p className="text-3xl font-bold text-blue-600">{inProgressCount}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full">
                                <Clock className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Resolved</p>
                                <p className="text-3xl font-bold text-green-600">{resolvedCount}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search inquiries..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value as any);
                                        setCurrentPage(1);
                                    }}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="all">All Status</option>
                                    <option value="new">New</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Inquiries Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl font-semibold text-gray-900">Inquiries ({filteredInquiries.length})</h1>
                            <button
                                onClick={() => refetch()}
                                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                                title="Refresh Inquiries"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Items per page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {filteredInquiries.length === 0 ? (
                    <div className="p-12 text-center">
                        <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No inquiries found</h3>
                        <p className="text-gray-600">No inquiries match your current filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listing</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedInquiries.map((inquiry) => (
                                    <tr key={inquiry.inquiry_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <Store className="w-4 h-4 text-gray-400" />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {inquiry.listing?.title || 'N/A'}
                                                    </div>
                                                    <div className="text-xs text-gray-500 capitalize">
                                                        {inquiry.listing?.listing_type || ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4 text-gray-400" />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {inquiry.user?.name || 'Guest'}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {inquiry.user?.email || inquiry.contact_email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900 max-w-xs truncate">
                                                {inquiry.message}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">{inquiry.contact_email}</div>
                                            {inquiry.contact_phone && (
                                                <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                                    <Phone className="w-3 h-3" />
                                                    {inquiry.contact_phone}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {getStatusBadge(inquiry.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(inquiry.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewInquiry(inquiry)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="View"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleRespond(inquiry)}
                                                    className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition-colors"
                                                    title="Respond"
                                                >
                                                    <MessageSquare className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mt-6">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing page {currentPage} of {totalPages} ({filteredInquiries.length} total inquiries)
                            </div>

                            <div className="flex items-center space-x-2">
                                {/* First Page */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="First page"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>

                                {/* Previous Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Previous page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center space-x-1">
                                    {getPageNumbers().map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                </div>

                                {/* Next Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Next page"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>

                                {/* Last Page */}
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Last page"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>


            {/* Reply Modal */}
            <Modal
                isOpen={showReplyModal}
                onClose={() => {
                    setShowReplyModal(false);
                    setSelectedInquiry(null);
                    setReplyMessage('');
                }}
                title="Respond to Inquiry"
            >
                {selectedInquiry && (
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                                value={replyStatus}
                                onChange={(e) => setReplyStatus(e.target.value as any)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="new">New</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Admin Response</label>
                            <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                rows={6}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter your response..."
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                onClick={() => {
                                    setShowReplyModal(false);
                                    setSelectedInquiry(null);
                                    setReplyMessage('');
                                }}
                                variant="outline"
                                disabled={isResponding}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSendReply}
                                variant="primary"
                                disabled={isResponding}
                                isLoading={isResponding}
                            >
                                Save Response
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default MarketplaceInquiries;

